import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  flashcard,
  QuizResult,
  selfStudyQuestion,
  StudyMaterialSetTag,
  AssistivContent,
  AssistivVideo,
  AssistivSource,
} from 'study-types';

interface STUDY_STATE_TYPE {
  flashcards: flashcard[];
  selfStudyQuestions: selfStudyQuestion[];
  favorited: Array<boolean>;
  savedID: string | null;
  currentPrompt: string;
  setName: string | null;
  isLoading: boolean;
  savedFlashcards: Array<StudyMaterialSetTag> | null;
  savedQuestions: Array<StudyMaterialSetTag> | null;
  savedSessions: Array<StudyMaterialSetTag> | null;
  quizResults: Array<QuizResult | null> | null;
  // Document upload state
  uploadedDocument: File | null;
  documentUrl: string | null;
  courseDocumentId: string | null;
  // Assistiv Flow specific state
  assistivContent: AssistivContent | null;
  activeAssistivTab: 'content' | 'videos' | 'sources';
}

const INITIAL_STATE: STUDY_STATE_TYPE = {
  flashcards: [],
  selfStudyQuestions: [],
  favorited: [],
  savedID: null,
  currentPrompt: '',
  setName: null,
  isLoading: false,
  savedFlashcards: null,
  savedQuestions: null,
  savedSessions: null,
  quizResults: [],
  // Document upload initial state
  uploadedDocument: null,
  documentUrl: null,
  courseDocumentId: null,
  // Assistiv Flow initial state
  assistivContent: null,
  activeAssistivTab: 'content',
};

const studySlice = createSlice({
  name: 'study',
  initialState: INITIAL_STATE,
  reducers: {
    setStudyFlashcards(state: STUDY_STATE_TYPE, action: PayloadAction<flashcard[]>) {
      state.flashcards = action.payload;
    },
    setStudyQuestions(state: STUDY_STATE_TYPE, action: PayloadAction<selfStudyQuestion[]>) {
      state.selfStudyQuestions = action.payload;
    },
    initializeFavorites(state: STUDY_STATE_TYPE, action: PayloadAction<number>) {
      if (action.payload > 0) {
        state.favorited = new Array(action.payload).fill(false);
      }
    },
    setFavorites(state: STUDY_STATE_TYPE, action: PayloadAction<Array<boolean>>) {
      state.favorited = action.payload;
    },
    setSavedID(state: STUDY_STATE_TYPE, action: PayloadAction<string | null>) {
      state.savedID = action.payload;
    },
    setCurrentPrompt(state: STUDY_STATE_TYPE, action: PayloadAction<string>) {
      state.currentPrompt = action.payload;
    },
    setSetName(state: STUDY_STATE_TYPE, action: PayloadAction<string | null>) {
      state.setName = action.payload;
    },
    setIsStudyMaterialLoading(state: STUDY_STATE_TYPE, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
    setSavedFlashcards(
      state: STUDY_STATE_TYPE,
      action: PayloadAction<Array<StudyMaterialSetTag> | null>,
    ) {
      state.savedFlashcards = action.payload;
    },
    setSavedQuestions(
      state: STUDY_STATE_TYPE,
      action: PayloadAction<Array<StudyMaterialSetTag> | null>,
    ) {
      state.savedQuestions = action.payload;
    },
    setSavedSessions(
      state: STUDY_STATE_TYPE,
      action: PayloadAction<Array<StudyMaterialSetTag> | null>,
    ) {
      state.savedSessions = action.payload;
    },
    setQuizResults(state: STUDY_STATE_TYPE, action: PayloadAction<Array<QuizResult | null>>) {
      state.quizResults = action.payload;
    },
    setUploadedDocument(state: STUDY_STATE_TYPE, action: PayloadAction<File | null>) {
      state.uploadedDocument = action.payload;
    },
    setDocumentUrl(state: STUDY_STATE_TYPE, action: PayloadAction<string | null>) {
      state.documentUrl = action.payload;
    },
    setCourseDocumentId(state: STUDY_STATE_TYPE, action: PayloadAction<string | null>) {
      state.courseDocumentId = action.payload;
    },
    resetActiveStudySlice(state: STUDY_STATE_TYPE) {
      state.flashcards = INITIAL_STATE.flashcards;
      state.selfStudyQuestions = INITIAL_STATE.selfStudyQuestions;
      state.favorited = INITIAL_STATE.favorited;
      state.savedID = INITIAL_STATE.savedID;
      state.currentPrompt = INITIAL_STATE.currentPrompt;
      state.setName = INITIAL_STATE.setName;
      state.quizResults = INITIAL_STATE.quizResults;
      state.uploadedDocument = INITIAL_STATE.uploadedDocument;
      state.documentUrl = INITIAL_STATE.documentUrl;
      state.courseDocumentId = INITIAL_STATE.courseDocumentId;
      state.assistivContent = INITIAL_STATE.assistivContent;
      state.activeAssistivTab = INITIAL_STATE.activeAssistivTab;
    },
    // Assistiv Flow specific reducers
    setAssistivContent(state: STUDY_STATE_TYPE, action: PayloadAction<AssistivContent>) {
      state.assistivContent = action.payload;
    },
    setActiveAssistivTab(
      state: STUDY_STATE_TYPE,
      action: PayloadAction<'content' | 'videos' | 'sources'>,
    ) {
      state.activeAssistivTab = action.payload;
    },
    appendAssistivContent(
      state: STUDY_STATE_TYPE,
      action: PayloadAction<{ markdown: string; sources: AssistivSource[] }>,
    ) {
      if (!state.assistivContent) return;
      // Insert clear divider before new markdown
      state.assistivContent.generatedContent =
        `${state.assistivContent.generatedContent}\n\n---\n\n${action.payload.markdown}`.trim();
      // Deduplicate sources by URL
      const existing = new Map(state.assistivContent.sources.map((s) => [s.url, s]));
      action.payload.sources.forEach((s) => {
        if (!existing.has(s.url)) {
          existing.set(s.url, s);
        }
      });
      state.assistivContent.sources = Array.from(existing.values());
    },
    clearAssistivContent(state: STUDY_STATE_TYPE) {
      state.assistivContent = null;
    },
  },
});

export const {
  setStudyFlashcards,
  setStudyQuestions,
  initializeFavorites,
  setFavorites,
  setQuizResults,
  setSavedID,
  setCurrentPrompt,
  setSetName,
  setIsStudyMaterialLoading,
  setSavedFlashcards,
  setSavedQuestions,
  setSavedSessions,
  setUploadedDocument,
  setDocumentUrl,
  setCourseDocumentId,
  resetActiveStudySlice,
  // Assistiv Flow actions
  setAssistivContent,
  setActiveAssistivTab,
  appendAssistivContent,
  clearAssistivContent,
} = studySlice.actions;

export default studySlice.reducer;
